import React, { useState, useMemo } from 'react';
import { getPlanBoundaries } from '../../utils/racePlanBoundaries';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Slider,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material';
import { tokens } from '../../theme';
import { useTranslation } from 'react-i18next';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, ReferenceArea } from 'recharts';
import { getUnits, formatDistance, formatPace, formatElevation } from '../../utils/units';
import { computeStaminaTrend } from '../../utils/staminaTrend';

const PlanBoundaryLabel = ({ viewBox, name, elapsed, color = '#fb923c' }) => {
  if (!viewBox) return null;
  const { x, y } = viewBox;
  return (
    <g transform={`translate(${x},${y}) rotate(-45)`}>
      <text x={5} y={-2} fill={color} fontSize={10} textAnchor="start">{name}</text>
      {elapsed && <text x={5} y={10} fill={color} fontSize={10} textAnchor="start">{elapsed}</text>}
    </g>
  );
};

// Map climb category → difficulty colour (yellow → orange → red, hardest = darkest red)
const CLIMB_CATEGORY_FILL = {
  'R-C4': '#ffe066',  // light yellow
  'R-C3': '#ffa020',  // amber / orange
  'R-C2': '#e05000',  // dark orange
  'R-C1': '#c01010',  // red
  'R-HC': '#7a0000',  // deep red
};

// Map descent category → blue-to-violet gradient palette
const DESCENT_CATEGORY_FILL = {
  'R-D4':  '#8ec5f5',  // sky blue
  'R-D3':  '#6898e8',  // cornflower blue
  'R-D2':  '#6b6ee0',  // blue-violet
  'R-D1':  '#8c48cc',  // indigo-violet
  'R-DHC': '#be35b8',  // vivid violet
};

const DEFAULT_METRICS = ['altitude', 'pace', 'heart_rate'];

// Keys that should never be smoothed (axis values, non-numeric flags)
const NO_SMOOTH_KEYS = new Set(['distance', 'time', 'altitude', 'index', 'elapsed_time_s']);

const formatElapsedTime = (totalSeconds) => {
  if (totalSeconds == null) return '';
  const s = Math.round(totalSeconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

const smoothData = (data, window) => {
  if (window <= 1 || data.length === 0) return data;
  const half = Math.floor(window / 2);
  return data.map((point, i) => {
    const out = { ...point };
    Object.keys(point).forEach(key => {
      if (NO_SMOOTH_KEYS.has(key) || point[key] == null) return;
      const start = Math.max(0, i - half);
      const end = Math.min(data.length - 1, i + half);
      const vals = [];
      for (let j = start; j <= end; j++) {
        if (data[j][key] != null) vals.push(data[j][key]);
      }
      out[key] = vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
    });
    return out;
  });
};

const RunningWorkoutChartsSection = ({ chunks, chartType = 'both', race = null, matchedCheckpoints = [], externalDistZoom = null, onDistZoomChange = null, climbs = [], descents = [], activeClimbRange = null, activeDescentRange = null, activeSegmentRange = null, defaultMetrics = DEFAULT_METRICS, racePlanSegments = [], liveMarker = null, predictedMarker = null }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [units, setUnits] = useState(getUnits());

  // Listen for unit changes
  React.useEffect(() => {
    const handleUnitsChange = (e) => {
      setUnits(e.detail || getUnits());
    };
    window.addEventListener('unitsChanged', handleUnitsChange);
    return () => {
      window.removeEventListener('unitsChanged', handleUnitsChange);
    };
  }, []);

  const [distanceChartMetrics, setDistanceChartMetrics] = useState(defaultMetrics);
  const [timeChartMetrics, setTimeChartMetrics] = useState(defaultMetrics);
  const [smoothingWindow, setSmoothingWindow] = useState(1);

  // Distance chart zoom state
  const [distZoomLeft, setDistZoomLeft] = useState('dataMin');
  const [distZoomRight, setDistZoomRight] = useState('dataMax');
  const [distRefLeft, setDistRefLeft] = useState(null);
  const [distRefRight, setDistRefRight] = useState(null);
  const [distSelecting, setDistSelecting] = useState(false);

  // Apply external zoom (e.g. from double-clicking a checkpoint row)
  React.useEffect(() => {
    if (!externalDistZoom) return;
    const { leftKm, rightKm } = externalDistZoom;
    const left = units === 'imperial' ? leftKm * 0.621371 : leftKm;
    const right = units === 'imperial' ? rightKm * 0.621371 : rightKm;
    setDistZoomLeft(parseFloat(left.toFixed(2)));
    setDistZoomRight(parseFloat(right.toFixed(2)));
  }, [externalDistZoom]); // eslint-disable-line react-hooks/exhaustive-deps

  // Notify parent whenever the visible distance range changes (always in km)
  React.useEffect(() => {
    if (!onDistZoomChange) return;
    if (distZoomLeft === 'dataMin' || distZoomRight === 'dataMax') {
      onDistZoomChange(null); // full extent — let map show everything
    } else {
      const toKm = v => units === 'imperial' ? v / 0.621371 : v;
      onDistZoomChange({ leftKm: toKm(distZoomLeft), rightKm: toKm(distZoomRight) });
    }
  }, [distZoomLeft, distZoomRight]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetDistZoom = () => {
    setDistZoomLeft('dataMin');
    setDistZoomRight('dataMax');
    setDistRefLeft(null);
    setDistRefRight(null);
  };

  const handleDistMouseDown = (e) => {
    if (e && e.activeLabel != null) {
      setDistRefLeft(e.activeLabel);
      setDistRefRight(e.activeLabel);
      setDistSelecting(true);
    }
  };

  const handleDistMouseMove = (e) => {
    if (distSelecting && e && e.activeLabel != null) {
      setDistRefRight(e.activeLabel);
    }
  };

  const handleDistMouseUp = () => {
    if (distSelecting && distRefLeft != null && distRefRight != null && distRefLeft !== distRefRight) {
      const left = Math.min(distRefLeft, distRefRight);
      const right = Math.max(distRefLeft, distRefRight);
      setDistZoomLeft(left);
      setDistZoomRight(right);
    }
    setDistRefLeft(null);
    setDistRefRight(null);
    setDistSelecting(false);
  };

  const chartLabels = {
    altitudeLabel: t('altitude', 'Altitude'),
    paceLabel: t('pace', 'Pace'),
    gapLabel: t('gap', 'Norm. Pace'),
    heartRateLabel: t('heartRate', 'Heart Rate'),
    cadenceLabel: t('cadence', 'Cadence'),
    gradientLabel: t('gradient', 'Gradient'),
    staminaScoreLabel: t('staminaTrendLabel', 'Stamina Trend'),
    movingPaceLabel: t('movingPace', 'Moving Pace'),
    movingGapLabel: t('movingGAP', 'Moving Norm. Pace'),
    movingAscVerticalSpeedLabel: t('movingAscVSpeed', 'Moving Asc V.Spd'),
    movingDescVerticalSpeedLabel: t('movingDescVSpeed', 'Moving Desc V.Spd'),
    distanceLabel: `${t('distance', 'Distance')} (${units === 'imperial' ? 'mi' : 'km'})`,
    timeLabel: t('time', 'Time')
  };

  // Helper functions (must be defined before useMemo)
  const convertDistance = (meters, units) => {
    return units === 'imperial' ? (meters * 0.000621371) : (meters / 1000);
  };

  const convertElevation = (meters, units) => {
    return units === 'imperial' ? (meters * 3.28084) : meters;
  };

  const formatPaceValue = (paceMinPerKm) => {
    if (!paceMinPerKm || paceMinPerKm === 0) return 'N/A';
    const pace = units === 'imperial' ? paceMinPerKm * 1.60934 : paceMinPerKm;
    const minutes = Math.floor(pace);
    const seconds = Math.round((pace - minutes) * 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}/${units === 'imperial' ? 'mi' : 'km'}`;
  };

  const formatMetricValue = (metric, value) => {
    if (value === null || value === undefined) return 'N/A';

    switch(metric) {
      case 'altitude':
        return `${value.toFixed(0)} ${units === 'imperial' ? 'ft' : 'm'}`;
      case 'pace':
      case 'gap':
      case 'moving_pace':
      case 'moving_gap':
        return formatPaceValue(value);
      case 'heart_rate':
        return `${Math.round(value)} bpm`;
      case 'cadence':
        return `${Math.round(value)} spm`;
      case 'moving_asc_vertical_speed':
      case 'moving_desc_vertical_speed':
        return `${value.toFixed(0)} m/h`;
      case 'gradient':
        return `${value.toFixed(1)}%`;
      case 'stamina_score':
        return `${value.toFixed(1)}`;
      default:
        return value.toFixed(2);
    }
  };

  // Smooth stamina TREND (polynomial fit) — plotted instead of the noisy raw
  // per-chunk score, matching the bike Stamina chart's trend line.
  const staminaTrend = useMemo(
    // Best-R² polynomial up to degree 6, matching the bike Stamina chart's trend.
    () => computeStaminaTrend((chunks || []).map((c) => (c.stamina_score ?? null)), { maxDegree: 6 }),
    [chunks]
  );

  // Prepare distance-based chart data
  const distanceChartData = useMemo(() => {
    if (!chunks || chunks.length === 0) return [];

    let cumulativeDistanceMeters = 0;
    let cumulativeTimeS = 0;

    return chunks.map((chunk, ci) => {
      const elapsedAtStart = cumulativeTimeS;
      cumulativeDistanceMeters += chunk.distance || 0;
      cumulativeTimeS += chunk.duration || 0;

      const isPaused = chunk.is_paused || (!chunk.avg_pace && chunk.distance === 0);
      return {
        distance: parseFloat(convertDistance(cumulativeDistanceMeters, units).toFixed(2)),
        elapsed_time_s: elapsedAtStart,
        altitude: convertElevation(chunk.avg_altitude, units),
        pace: isPaused ? null : (chunk.avg_pace || null),
        gap: isPaused ? null : (chunk.gap || null),
        heart_rate: chunk.avg_heart_rate || null,
        cadence: chunk.avg_cadence || null,
        vertical_speed: isPaused ? null : ((chunk.avg_vertical_speed || 0) * 3600),
        gradient: isPaused ? null : (chunk.avg_gradient || 0),
        stamina_score: staminaTrend[ci] ?? null,
        moving_pace: isPaused ? null : (chunk.moving_pace || null),
        moving_gap: isPaused ? null : (chunk.moving_gap || null),
        moving_vertical_speed: chunk.moving_vertical_speed != null ? chunk.moving_vertical_speed * 3600 : null,
        moving_asc_vertical_speed: chunk.moving_asc_vertical_speed != null ? chunk.moving_asc_vertical_speed * 3600 : null,
        moving_desc_vertical_speed: chunk.moving_desc_vertical_speed != null ? chunk.moving_desc_vertical_speed * 3600 : null,
      };
    });
  }, [chunks, units, staminaTrend]);

  // Generate x-axis ticks at 5km intervals
  const distanceAxisTicks = useMemo(() => {
    if (distanceChartData.length === 0) return [];
    const maxDistance = distanceChartData[distanceChartData.length - 1].distance;
    const ticks = [];
    const interval = units === 'imperial' ? 5 * 0.621371 : 5; // 5km or ~3.1 miles
    for (let i = 0; i <= maxDistance; i += interval) {
      ticks.push(Math.round(i));
    }
    // Add the max distance if it's not already included
    if (ticks[ticks.length - 1] < maxDistance) {
      ticks.push(Math.ceil(maxDistance));
    }
    return ticks;
  }, [distanceChartData, units]);

  // Prepare time-based chart data
  const timeChartData = useMemo(() => {
    if (!chunks || chunks.length === 0) return [];

    return chunks.map((chunk, ci) => {
      const timeMinutes = (chunk.timestamp || 0) / 60;

      const isPaused = chunk.is_paused || (!chunk.avg_pace && chunk.distance === 0);
      return {
        time: timeMinutes,
        altitude: convertElevation(chunk.avg_altitude, units),
        pace: isPaused ? null : (chunk.avg_pace || null),
        gap: isPaused ? null : (chunk.gap || null),
        heart_rate: chunk.avg_heart_rate || null,
        cadence: chunk.avg_cadence || null,
        vertical_speed: isPaused ? null : ((chunk.avg_vertical_speed || 0) * 3600),
        gradient: isPaused ? null : (chunk.avg_gradient || 0),
        stamina_score: staminaTrend[ci] ?? null,
        moving_pace: isPaused ? null : (chunk.moving_pace || null),
        moving_gap: isPaused ? null : (chunk.moving_gap || null),
        moving_vertical_speed: chunk.moving_vertical_speed != null ? chunk.moving_vertical_speed * 3600 : null,
        moving_asc_vertical_speed: chunk.moving_asc_vertical_speed != null ? chunk.moving_asc_vertical_speed * 3600 : null,
        moving_desc_vertical_speed: chunk.moving_desc_vertical_speed != null ? chunk.moving_desc_vertical_speed * 3600 : null,
      };
    });
  }, [chunks, units, staminaTrend]);

  const smoothedDistanceChartData = useMemo(() => smoothData(distanceChartData, smoothingWindow), [distanceChartData, smoothingWindow]);
  const smoothedTimeChartData = useMemo(() => smoothData(timeChartData, smoothingWindow), [timeChartData, smoothingWindow]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, isTimeChart }) => {
    if (!active || !payload || payload.length === 0) return null;

    return (
      <Box
        sx={{
          backgroundColor: colors.grey[800],
          border: `1px solid ${colors.grey[700]}`,
          borderRadius: '4px',
          p: 1.5
        }}
      >
        <Typography variant="body2" fontWeight="bold" color={colors.grey[100]} mb={0.5}>
          {isTimeChart
            ? `Time: ${payload[0].payload.time.toFixed(1)} min`
            : `Distance: ${payload[0].payload.distance.toFixed(2)} ${units === 'imperial' ? 'mi' : 'km'}`
          }
        </Typography>
        {!isTimeChart && payload[0].payload.elapsed_time_s != null && (
          <Typography variant="body2" color={colors.grey[400]} mb={0.5}>
            Elapsed: {formatElapsedTime(payload[0].payload.elapsed_time_s)}
          </Typography>
        )}
        {payload.map((entry, index) => (
          <Typography key={index} variant="body2" color={entry.color}>
            {entry.name}: {formatMetricValue(entry.dataKey, entry.value)}
          </Typography>
        ))}
      </Box>
    );
  };

  if (!chunks || chunks.length === 0) {
    return null;
  }

  return (
    <>
      {/* Distance-Based Chart */}
      {(chartType === 'both' || chartType === 'distance') && (
      <Box mt={chartType === 'both' ? 4 : 0}>
        <Paper
          elevation={0}
          sx={{
            p: isMobile ? 1 : 3,
            backgroundColor: colors.grey[800],
            borderRadius: "8px"
          }}
        >
          <Box display="flex" flexDirection={isMobile ? "column" : "row"} justifyContent="space-between" alignItems={isMobile ? "flex-start" : "center"} mb={3} gap={isMobile ? 2 : 0}>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color={colors.grey[100]}>
                {t('distanceBasedAnalysis', 'Distance-Based Analysis')}
              </Typography>
              {(distZoomLeft !== 'dataMin' || distZoomRight !== 'dataMax') && (
                <button
                  onClick={resetDistZoom}
                  style={{
                    background: colors.grey[700],
                    color: colors.grey[100],
                    border: `1px solid ${colors.grey[500]}`,
                    borderRadius: 4,
                    padding: '2px 10px',
                    cursor: 'pointer',
                    fontSize: '0.75rem'
                  }}
                >
                  {t('resetZoom', 'Reset Zoom')}
                </button>
              )}
            </Box>
            <Box display="flex" alignItems="center" gap={3} flexDirection={isMobile ? "column" : "row"} width={isMobile ? '100%' : 'auto'}>
              <Box sx={{
                position: 'relative',
                border: `1px solid ${colors.grey[600]}`,
                borderRadius: '4px',
                px: 2,
                pt: 1,
                pb: 1,
                minWidth: 200,
              }}>
                <Typography variant="caption" sx={{
                  position: 'absolute',
                  top: -10,
                  left: 10,
                  px: 0.5,
                  backgroundColor: colors.grey[800],
                  color: colors.grey[300],
                  fontSize: '0.75rem',
                }}>
                  {smoothingWindow === 1 ? t('smoothingOff', 'Smoothing (off)') : t('smoothingChunks', 'Smoothing ({{count}} chunks)', { count: smoothingWindow })}
                </Typography>
                <Slider
                  value={smoothingWindow}
                  min={1}
                  max={20}
                  step={1}
                  onChange={(_, v) => setSmoothingWindow(v)}
                  sx={{ color: colors.blueAccent[400], display: 'block', mt: 0.5 }}
                  size="small"
                />
              </Box>
              <FormControl sx={{ minWidth: isMobile ? '100%' : 300, width: isMobile ? '100%' : 'auto' }}>
              <InputLabel sx={{ color: colors.grey[300] }}>{t('selectMetrics', 'Select Metrics')}</InputLabel>
              <Select
                multiple
                value={distanceChartMetrics}
                onChange={(e) => setDistanceChartMetrics(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                input={<OutlinedInput label={t('selectMetrics', 'Select Metrics')} />}
                renderValue={(selected) => selected.map(m =>
                  m === 'altitude' ? chartLabels.altitudeLabel :
                  m === 'pace' ? chartLabels.paceLabel :
                  m === 'gap' ? chartLabels.gapLabel :
                  m === 'heart_rate' ? chartLabels.heartRateLabel :
                  m === 'cadence' ? chartLabels.cadenceLabel :
                  m === 'gradient' ? chartLabels.gradientLabel :
                  m === 'stamina_score' ? chartLabels.staminaScoreLabel :
                  m === 'moving_pace' ? chartLabels.movingPaceLabel :
                  m === 'moving_gap' ? chartLabels.movingGapLabel :
                  m === 'moving_asc_vertical_speed' ? chartLabels.movingAscVerticalSpeedLabel :
                  m === 'moving_desc_vertical_speed' ? chartLabels.movingDescVerticalSpeedLabel :
                  m
                ).join(', ')}
                sx={{
                  color: colors.grey[100],
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                }}
              >
                <MenuItem value="altitude">
                  <Checkbox checked={distanceChartMetrics.indexOf('altitude') > -1} />
                  <ListItemText primary={chartLabels.altitudeLabel} />
                </MenuItem>
                <MenuItem value="pace">
                  <Checkbox checked={distanceChartMetrics.indexOf('pace') > -1} />
                  <ListItemText primary={chartLabels.paceLabel} />
                </MenuItem>
                <MenuItem value="gap">
                  <Checkbox checked={distanceChartMetrics.indexOf('gap') > -1} />
                  <ListItemText primary={chartLabels.gapLabel} />
                </MenuItem>
                <MenuItem value="heart_rate">
                  <Checkbox checked={distanceChartMetrics.indexOf('heart_rate') > -1} />
                  <ListItemText primary={chartLabels.heartRateLabel} />
                </MenuItem>
                <MenuItem value="cadence">
                  <Checkbox checked={distanceChartMetrics.indexOf('cadence') > -1} />
                  <ListItemText primary={chartLabels.cadenceLabel} />
                </MenuItem>
                <MenuItem value="gradient">
                  <Checkbox checked={distanceChartMetrics.indexOf('gradient') > -1} />
                  <ListItemText primary={chartLabels.gradientLabel} />
                </MenuItem>
                <MenuItem value="stamina_score">
                  <Checkbox checked={distanceChartMetrics.indexOf('stamina_score') > -1} />
                  <ListItemText primary={chartLabels.staminaScoreLabel} />
                </MenuItem>
                <MenuItem value="moving_pace">
                  <Checkbox checked={distanceChartMetrics.indexOf('moving_pace') > -1} />
                  <ListItemText primary={chartLabels.movingPaceLabel} />
                </MenuItem>
                <MenuItem value="moving_gap">
                  <Checkbox checked={distanceChartMetrics.indexOf('moving_gap') > -1} />
                  <ListItemText primary={chartLabels.movingGapLabel} />
                </MenuItem>
                <MenuItem value="moving_asc_vertical_speed">
                  <Checkbox checked={distanceChartMetrics.indexOf('moving_asc_vertical_speed') > -1} />
                  <ListItemText primary={chartLabels.movingAscVerticalSpeedLabel} />
                </MenuItem>
                <MenuItem value="moving_desc_vertical_speed">
                  <Checkbox checked={distanceChartMetrics.indexOf('moving_desc_vertical_speed') > -1} />
                  <ListItemText primary={chartLabels.movingDescVerticalSpeedLabel} />
                </MenuItem>
              </Select>
            </FormControl>
            </Box>
          </Box>

          <Typography variant="caption" color={colors.grey[400]} mb={1} display="block">
            {t('dragToZoom', 'Drag on the chart to zoom in')}
          </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart
              data={smoothedDistanceChartData}
              margin={isMobile ? { top: 55, right: 5, left: 0, bottom: 10 } : { top: 55, right: 60, left: 10, bottom: 10 }}
              onMouseDown={handleDistMouseDown}
              onMouseMove={handleDistMouseMove}
              onMouseUp={handleDistMouseUp}
              onMouseLeave={handleDistMouseUp}
              style={{ userSelect: 'none' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[700]} />
              <XAxis
                dataKey="distance"
                type="number"
                domain={[distZoomLeft, distZoomRight]}
                allowDataOverflow={true}
                ticks={distanceAxisTicks}
                stroke={colors.grey[100]}
              />
              {/* Left Y-axis for altitude only */}
              <YAxis
                yAxisId="left"
                stroke={colors.grey[100]}
                width={isMobile ? 0 : 60}
                tick={!isMobile}
                axisLine={!isMobile}
                tickLine={!isMobile}
                label={isMobile ? undefined : { value: t('altitude', 'Altitude'), angle: -90, position: 'insideLeft', fill: colors.grey[300] }}
              />
              {/* Right Y-axis for all other metrics */}
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke={colors.grey[100]}
                width={isMobile ? 0 : 60}
                tick={!isMobile}
                axisLine={!isMobile}
                tickLine={!isMobile}
                label={isMobile ? undefined : { value: t('metricsYAxis', 'Metrics'), angle: 90, position: 'insideRight', fill: colors.grey[300] }}
              />
              <Tooltip content={<CustomTooltip isTimeChart={false} />} />
              <Legend wrapperStyle={{ color: colors.grey[100] }} verticalAlign="bottom" />
              {distanceChartMetrics.includes('altitude') && (
                <Line yAxisId="left" type="monotone" dataKey="altitude" stroke={colors.chartColors[1]} name={chartLabels.altitudeLabel} dot={false} />
              )}
              {distanceChartMetrics.includes('pace') && (
                <Line yAxisId="right" type="monotone" dataKey="pace" stroke={colors.chartColors[2]} name={chartLabels.paceLabel} dot={false} />
              )}
              {distanceChartMetrics.includes('gap') && (
                <Line yAxisId="right" type="monotone" dataKey="gap" stroke={colors.chartColors[4]} name={chartLabels.gapLabel} dot={false} />
              )}
              {distanceChartMetrics.includes('heart_rate') && (
                <Line yAxisId="right" type="monotone" dataKey="heart_rate" stroke={colors.redAccent[500]} name={chartLabels.heartRateLabel} dot={false} />
              )}
              {distanceChartMetrics.includes('cadence') && (
                <Line yAxisId="right" type="monotone" dataKey="cadence" stroke={colors.chartColors[9]} name={chartLabels.cadenceLabel} dot={false} />
              )}
              {distanceChartMetrics.includes('gradient') && (
                <Line yAxisId="right" type="monotone" dataKey="gradient" stroke={colors.chartColors[3]} name={chartLabels.gradientLabel} dot={false} />
              )}
              {distanceChartMetrics.includes('stamina_score') && (
                <Line yAxisId="right" type="monotone" dataKey="stamina_score" stroke={colors.greenAccent[500]} name={chartLabels.staminaScoreLabel} dot={false} />
              )}
              {distanceChartMetrics.includes('moving_pace') && (
                <Line yAxisId="right" type="monotone" dataKey="moving_pace" stroke={colors.chartColors[2]} strokeDasharray="5 3" name={chartLabels.movingPaceLabel} dot={false} />
              )}
              {distanceChartMetrics.includes('moving_gap') && (
                <Line yAxisId="right" type="monotone" dataKey="moving_gap" stroke={colors.chartColors[4]} strokeDasharray="5 3" name={chartLabels.movingGapLabel} dot={false} />
              )}
              {distanceChartMetrics.includes('moving_asc_vertical_speed') && (
                <Line yAxisId="right" type="monotone" dataKey="moving_asc_vertical_speed" stroke={colors.greenAccent[500]} name={chartLabels.movingAscVerticalSpeedLabel} dot={false} strokeWidth={2} />
              )}
              {distanceChartMetrics.includes('moving_desc_vertical_speed') && (
                <Line yAxisId="right" type="monotone" dataKey="moving_desc_vertical_speed" stroke={colors.blueAccent[500]} name={chartLabels.movingDescVerticalSpeedLabel} dot={false} strokeWidth={2} />
              )}

              {/* Checkpoint / unplanned-stop markers */}
              {matchedCheckpoints && (() => {
                const cpElapsedMap = {};
                (racePlanSegments || []).forEach(seg => {
                  if (seg.elapsed_s != null && seg.end_label) {
                    cpElapsedMap[seg.end_label] = seg.elapsed_s;
                  }
                });
                return matchedCheckpoints.map((checkpoint, idx) => {
                  if (checkpoint.status === 'undetected' || checkpoint.fit_distance_km == null) return null;
                  const checkpointDistance = units === 'imperial'
                    ? checkpoint.fit_distance_km * 0.621371
                    : checkpoint.fit_distance_km;
                  const maxDistance = distanceChartData.length > 0
                    ? distanceChartData[distanceChartData.length - 1].distance
                    : 0;
                  if (checkpointDistance > maxDistance + 1) return null;
                  const isUnplanned = checkpoint.type === 'unplanned_stop';
                  const lineColor = isUnplanned ? '#ff9800' : colors.greenAccent[500];
                  let elapsedStr = null;
                  if (!isMobile) {
                    const elapsed_s = cpElapsedMap[checkpoint.name];
                    if (elapsed_s != null) {
                      const h = Math.floor(elapsed_s / 3600);
                      const m = Math.floor((elapsed_s % 3600) / 60);
                      elapsedStr = h > 0 ? `${h}h${String(m).padStart(2, '0')}m` : `${m}m`;
                    }
                  }
                  return (
                    <ReferenceLine
                      key={`checkpoint-${checkpoint.name}-${idx}`}
                      x={checkpointDistance}
                      yAxisId="left"
                      stroke={lineColor}
                      strokeWidth={isUnplanned ? 1.5 : 2}
                      strokeDasharray={isUnplanned ? "3 3" : "5 5"}
                      label={<PlanBoundaryLabel name={checkpoint.name} elapsed={elapsedStr} color={lineColor} />}
                    />
                  );
                });
              })()}

              {/* Predicted "where they should be right now" marker — solid blue, same pairing as the map */}
              {predictedMarker && predictedMarker.distanceKm != null && (() => {
                const x = units === 'imperial' ? predictedMarker.distanceKm * 0.621371 : predictedMarker.distanceKm;
                return (
                  <ReferenceLine
                    x={x}
                    yAxisId="left"
                    stroke="#2196f3"
                    strokeWidth={2.5}
                    label={<PlanBoundaryLabel name={predictedMarker.label || 'Predicted now'} color="#2196f3" />}
                  />
                );
              })()}

              {/* Live "last seen" marker — distinct solid red line, drawn above checkpoint markers */}
              {liveMarker && liveMarker.distanceKm != null && (() => {
                const x = units === 'imperial' ? liveMarker.distanceKm * 0.621371 : liveMarker.distanceKm;
                return (
                  <ReferenceLine
                    x={x}
                    yAxisId="left"
                    stroke="#f44336"
                    strokeWidth={2.5}
                    label={<PlanBoundaryLabel name={liveMarker.label || 'Last seen'} color="#f44336" />}
                  />
                );
              })()}

              {/* Plan segment boundary markers (orange) */}
              {racePlanSegments.length > 0 && (() => {
                // Shared with the Plan Map so both mark the same places —
                // see utils/racePlanBoundaries.js.
                const boundaries = getPlanBoundaries(racePlanSegments, matchedCheckpoints);
                return boundaries.map(({ distKm, label, elapsed_s }, idx) => {
                  const x = units === 'imperial' ? distKm * 0.621371 : distKm;
                  let elapsedStr = null;
                  if (!isMobile && elapsed_s != null) {
                    const h = Math.floor(elapsed_s / 3600);
                    const m = Math.floor((elapsed_s % 3600) / 60);
                    elapsedStr = h > 0 ? `${h}h${String(m).padStart(2, '0')}m` : `${m}m`;
                  }
                  return (
                    <ReferenceLine
                      key={`plan-boundary-${idx}`}
                      x={x}
                      yAxisId="left"
                      stroke="#fb923c"
                      strokeWidth={1}
                      strokeDasharray="4 3"
                      label={<PlanBoundaryLabel name={label} elapsed={elapsedStr} />}
                    />
                  );
                });
              })()}

              {/* Climb category overlays */}
              {climbs && climbs.map((climb, i) => {
                const toDisplay = km => units === 'imperial' ? km * 0.621371 : km;
                const x1 = toDisplay(climb.start_distance_m / 1000);
                const x2 = toDisplay(climb.end_distance_m / 1000);
                const fill = CLIMB_CATEGORY_FILL[climb.category] || CLIMB_CATEGORY_FILL['R-C4'];
                return (
                  <ReferenceArea
                    key={`climb-${i}`}
                    yAxisId="left"
                    x1={x1}
                    x2={x2}
                    fill={fill}
                    fillOpacity={0.22}
                    stroke={fill}
                    strokeOpacity={0.5}
                    strokeWidth={1}
                    ifOverflow="hidden"
                  />
                );
              })}

              {/* Descent category overlays */}
              {descents && descents.map((descent, i) => {
                const toDisplay = km => units === 'imperial' ? km * 0.621371 : km;
                const x1 = toDisplay(descent.start_distance_m / 1000);
                const x2 = toDisplay(descent.end_distance_m / 1000);
                const fill = DESCENT_CATEGORY_FILL[descent.category] || DESCENT_CATEGORY_FILL['R-D4'];
                return (
                  <ReferenceArea
                    key={`descent-${i}`}
                    yAxisId="left"
                    x1={x1}
                    x2={x2}
                    fill={fill}
                    fillOpacity={0.22}
                    stroke={fill}
                    strokeOpacity={0.5}
                    strokeWidth={1}
                    ifOverflow="hidden"
                  />
                );
              })}

              {/* Active climb highlight */}
              {activeClimbRange && (() => {
                const toDisplay = km => units === 'imperial' ? km * 0.621371 : km;
                const x1 = toDisplay(activeClimbRange.leftKm);
                const x2 = toDisplay(activeClimbRange.rightKm);
                const c  = activeClimbRange.color || '#e05000';
                return (
                  <ReferenceArea
                    yAxisId="left"
                    x1={x1}
                    x2={x2}
                    fill={c}
                    fillOpacity={0.35}
                    stroke={c}
                    strokeOpacity={0.9}
                    strokeWidth={2}
                    ifOverflow="hidden"
                  />
                );
              })()}

              {/* Active descent highlight — uses the twistiness colour of the clicked row */}
              {activeDescentRange && (() => {
                const toDisplay = km => units === 'imperial' ? km * 0.621371 : km;
                const x1 = toDisplay(activeDescentRange.leftKm);
                const x2 = toDisplay(activeDescentRange.rightKm);
                const c  = activeDescentRange.color || '#6898e8';
                return (
                  <ReferenceArea
                    yAxisId="left"
                    x1={x1}
                    x2={x2}
                    fill={c}
                    fillOpacity={0.35}
                    stroke={c}
                    strokeOpacity={0.9}
                    strokeWidth={2}
                    ifOverflow="hidden"
                  />
                );
              })()}

              {/* Selected segment highlight (single-click from Race Plan grid) */}
              {activeSegmentRange && (() => {
                const toDisplay = km => units === 'imperial' ? km * 0.621371 : km;
                const x1 = toDisplay(activeSegmentRange.leftKm);
                const x2 = toDisplay(activeSegmentRange.rightKm);
                return (
                  <ReferenceArea
                    yAxisId="left"
                    x1={x1}
                    x2={x2}
                    fill="#ffeb3b"
                    fillOpacity={0.30}
                    stroke="#f9a825"
                    strokeOpacity={0.85}
                    strokeWidth={2}
                    ifOverflow="hidden"
                  />
                );
              })()}

              {distRefLeft != null && distRefRight != null && (
                <ReferenceArea
                  yAxisId="left"
                  x1={Math.min(distRefLeft, distRefRight)}
                  x2={Math.max(distRefLeft, distRefRight)}
                  strokeOpacity={0.3}
                  fill={colors.blueAccent[700]}
                  fillOpacity={0.3}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      </Box>
      )}

      {/* Time-Based Chart — hidden for now */}
      {false && (chartType === 'both' || chartType === 'time') && (
      <Box mt={4}>
        <Paper
          elevation={0}
          sx={{
            p: isMobile ? 1 : 3,
            backgroundColor: colors.grey[800],
            borderRadius: "8px"
          }}
        >
          <Box display="flex" flexDirection={isMobile ? "column" : "row"} justifyContent="space-between" alignItems={isMobile ? "flex-start" : "center"} mb={3} gap={isMobile ? 2 : 0}>
            <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color={colors.grey[100]}>
              {t('timeBasedAnalysis', 'Time-Based Analysis')}
            </Typography>
            <FormControl sx={{ minWidth: isMobile ? '100%' : 300, width: isMobile ? '100%' : 'auto' }}>
              <InputLabel sx={{ color: colors.grey[300] }}>{t('selectMetrics', 'Select Metrics')}</InputLabel>
              <Select
                multiple
                value={timeChartMetrics}
                onChange={(e) => setTimeChartMetrics(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                input={<OutlinedInput label={t('selectMetrics', 'Select Metrics')} />}
                renderValue={(selected) => selected.map(m =>
                  m === 'altitude' ? chartLabels.altitudeLabel :
                  m === 'pace' ? chartLabels.paceLabel :
                  m === 'gap' ? chartLabels.gapLabel :
                  m === 'heart_rate' ? chartLabels.heartRateLabel :
                  m === 'cadence' ? chartLabels.cadenceLabel :
                  m === 'gradient' ? chartLabels.gradientLabel :
                  m === 'stamina_score' ? chartLabels.staminaScoreLabel :
                  m === 'moving_pace' ? chartLabels.movingPaceLabel :
                  m === 'moving_gap' ? chartLabels.movingGapLabel :
                  m === 'moving_asc_vertical_speed' ? chartLabels.movingAscVerticalSpeedLabel :
                  m === 'moving_desc_vertical_speed' ? chartLabels.movingDescVerticalSpeedLabel :
                  m
                ).join(', ')}
                sx={{
                  color: colors.grey[100],
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                }}
              >
                <MenuItem value="altitude">
                  <Checkbox checked={timeChartMetrics.indexOf('altitude') > -1} />
                  <ListItemText primary={chartLabels.altitudeLabel} />
                </MenuItem>
                <MenuItem value="pace">
                  <Checkbox checked={timeChartMetrics.indexOf('pace') > -1} />
                  <ListItemText primary={chartLabels.paceLabel} />
                </MenuItem>
                <MenuItem value="gap">
                  <Checkbox checked={timeChartMetrics.indexOf('gap') > -1} />
                  <ListItemText primary={chartLabels.gapLabel} />
                </MenuItem>
                <MenuItem value="heart_rate">
                  <Checkbox checked={timeChartMetrics.indexOf('heart_rate') > -1} />
                  <ListItemText primary={chartLabels.heartRateLabel} />
                </MenuItem>
                <MenuItem value="cadence">
                  <Checkbox checked={timeChartMetrics.indexOf('cadence') > -1} />
                  <ListItemText primary={chartLabels.cadenceLabel} />
                </MenuItem>
                <MenuItem value="gradient">
                  <Checkbox checked={timeChartMetrics.indexOf('gradient') > -1} />
                  <ListItemText primary={chartLabels.gradientLabel} />
                </MenuItem>
                <MenuItem value="stamina_score">
                  <Checkbox checked={timeChartMetrics.indexOf('stamina_score') > -1} />
                  <ListItemText primary={chartLabels.staminaScoreLabel} />
                </MenuItem>
                <MenuItem value="moving_pace">
                  <Checkbox checked={timeChartMetrics.indexOf('moving_pace') > -1} />
                  <ListItemText primary={chartLabels.movingPaceLabel} />
                </MenuItem>
                <MenuItem value="moving_gap">
                  <Checkbox checked={timeChartMetrics.indexOf('moving_gap') > -1} />
                  <ListItemText primary={chartLabels.movingGapLabel} />
                </MenuItem>
                <MenuItem value="moving_asc_vertical_speed">
                  <Checkbox checked={timeChartMetrics.indexOf('moving_asc_vertical_speed') > -1} />
                  <ListItemText primary={chartLabels.movingAscVerticalSpeedLabel} />
                </MenuItem>
                <MenuItem value="moving_desc_vertical_speed">
                  <Checkbox checked={timeChartMetrics.indexOf('moving_desc_vertical_speed') > -1} />
                  <ListItemText primary={chartLabels.movingDescVerticalSpeedLabel} />
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={smoothedTimeChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[700]} />
              <XAxis
                dataKey="time"
                stroke={colors.grey[100]}
                label={{ value: `${chartLabels.timeLabel} (min)`, position: 'insideBottom', offset: -5, fill: colors.grey[300] }}
              />
              {/* Left Y-axis for altitude only */}
              <YAxis
                yAxisId="left"
                stroke={colors.grey[100]}
                width={isMobile ? 0 : 60}
                tick={!isMobile}
                axisLine={!isMobile}
                tickLine={!isMobile}
                label={isMobile ? undefined : { value: t('altitude', 'Altitude'), angle: -90, position: 'insideLeft', fill: colors.grey[300] }}
              />
              {/* Right Y-axis for all other metrics */}
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke={colors.grey[100]}
                width={isMobile ? 0 : 60}
                tick={!isMobile}
                axisLine={!isMobile}
                tickLine={!isMobile}
                label={isMobile ? undefined : { value: t('metricsYAxis', 'Metrics'), angle: 90, position: 'insideRight', fill: colors.grey[300] }}
              />
              <Tooltip content={<CustomTooltip isTimeChart={true} />} />
              <Legend wrapperStyle={{ color: colors.grey[100] }} verticalAlign="bottom" />
              {timeChartMetrics.includes('altitude') && (
                <Line yAxisId="left" type="monotone" dataKey="altitude" stroke={colors.chartColors[1]} name={chartLabels.altitudeLabel} dot={false} />
              )}
              {timeChartMetrics.includes('pace') && (
                <Line yAxisId="right" type="monotone" dataKey="pace" stroke={colors.chartColors[2]} name={chartLabels.paceLabel} dot={false} />
              )}
              {timeChartMetrics.includes('gap') && (
                <Line yAxisId="right" type="monotone" dataKey="gap" stroke={colors.chartColors[4]} name={chartLabels.gapLabel} dot={false} />
              )}
              {timeChartMetrics.includes('heart_rate') && (
                <Line yAxisId="right" type="monotone" dataKey="heart_rate" stroke={colors.redAccent[500]} name={chartLabels.heartRateLabel} dot={false} />
              )}
              {timeChartMetrics.includes('cadence') && (
                <Line yAxisId="right" type="monotone" dataKey="cadence" stroke={colors.chartColors[9]} name={chartLabels.cadenceLabel} dot={false} />
              )}
              {timeChartMetrics.includes('gradient') && (
                <Line yAxisId="right" type="monotone" dataKey="gradient" stroke={colors.chartColors[3]} name={chartLabels.gradientLabel} dot={false} />
              )}
              {timeChartMetrics.includes('stamina_score') && (
                <Line yAxisId="right" type="monotone" dataKey="stamina_score" stroke={colors.greenAccent[500]} name={chartLabels.staminaScoreLabel} dot={false} />
              )}
              {timeChartMetrics.includes('moving_pace') && (
                <Line yAxisId="right" type="monotone" dataKey="moving_pace" stroke={colors.chartColors[2]} strokeDasharray="5 3" name={chartLabels.movingPaceLabel} dot={false} />
              )}
              {timeChartMetrics.includes('moving_gap') && (
                <Line yAxisId="right" type="monotone" dataKey="moving_gap" stroke={colors.chartColors[4]} strokeDasharray="5 3" name={chartLabels.movingGapLabel} dot={false} />
              )}
              {timeChartMetrics.includes('moving_asc_vertical_speed') && (
                <Line yAxisId="right" type="monotone" dataKey="moving_asc_vertical_speed" stroke={colors.greenAccent[500]} name={chartLabels.movingAscVerticalSpeedLabel} dot={false} strokeWidth={2} />
              )}
              {timeChartMetrics.includes('moving_desc_vertical_speed') && (
                <Line yAxisId="right" type="monotone" dataKey="moving_desc_vertical_speed" stroke={colors.blueAccent[500]} name={chartLabels.movingDescVerticalSpeedLabel} dot={false} strokeWidth={2} />
              )}
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      </Box>
      )}
    </>
  );
};

export default RunningWorkoutChartsSection;
